// Angular
import { NgModule } from '@angular/core';
import { type Routes, RouterModule, UrlSegment, type UrlMatchResult } from '@angular/router';
// Medsurf Lib
import * as FlatModels from '@medsurf/flat-models';
import * as FlatGuards from '@medsurf/flat-guards';
import * as FlatResolver from '@medsurf/flat-resolvers';
// Components
// -- Error
import { ErrorsComponent } from "./errors/errors.component";
// -- User
import { LoginComponent } from './user/login/login.component';
// -- Change Log
import { ChangelogComponent } from './changelog/changelog.component';
// -- Chapter Slide
import { ChapterSlideComponent } from './chapter-slide/chapter-slide.component';
// -- Admin
import { UsersComponent } from './admin/users/users.component';
import { UserDetailComponent } from './admin/user-detail/user-detail.component';
import { MediaLibraryComponent } from './admin/media-library/media-library.component';
import { MatomoStatsComponent } from './admin/matomo-stats/matomo-stats.component';
import { DetachedPagesComponent } from './admin/detached-pages/detached-pages.component';
import { MetadataComponent } from './admin/metadata/metadata.component';
import { ModalitiesComponent } from './admin/modalities/modalities.component';
import { ModalityDetailComponent } from './admin/modality-detail/modality-detail.component';
import { StainsComponent } from './admin/stains/stains.component';
import { StainDetailComponent } from './admin/stain-detail/stain-detail.component';
import { CopyrightDetailComponent } from './admin/copyright-detail/copyright-detail.component';
import { CopyrightsComponent } from './admin/copyrights/copyrights.component';
// -- Slides
import { SlideComponent } from "./slide/slide.component";
// -- Search
import { SearchComponent } from './search/search.component';
import { ImportStatsComponent } from './admin/import-stats/import-stats.component';

const navigationMatcher = (url: UrlSegment[]): UrlMatchResult | null => {
  if (url.length <= 5) {
    const emptyUrlSegment = new UrlSegment('', {});
    return {
      consumed: url,
      posParams: {
        eLearningSlug: url[0] || emptyUrlSegment,
        subjectSlug: url[1] || emptyUrlSegment,
        chapterSlug: url[2] || emptyUrlSegment,
        blockSlug: url[3] || emptyUrlSegment,
        slideSlug: url[4] || emptyUrlSegment,
      }
    };
  }
  return null;
};

const appRoutes: Routes = [
  // Errors
  {
    path: '404',
    component: ErrorsComponent,
    canActivate: [
      FlatGuards.NavigationControlGuard
    ],
    data: {
      title: 'global.site_not_found',
      errorType: 404,
    }
  },
  {
    path: '401',
    component: ErrorsComponent,
    canActivate: [
      FlatGuards.NavigationControlGuard
    ],
    data: {
      title: 'global.not_authorized',
      errorType: 401,
    }
  },
  // User
  {
    path: 'login',
    component: LoginComponent,
    canActivate: [
      FlatGuards.LoginAuthorGuard,
      FlatGuards.NavigationControlGuard
    ],
    data: {
      title: 'global.login',
    }
  },
  // Change Log
  {
    path: 'changelog',
    component: ChangelogComponent,
    canActivate: [
      FlatGuards.UserAuthorGuard,
      FlatGuards.NavigationControlGuard,
    ],
    data: {
      title: 'global.changelog',
    },
    resolve: {
      releases: FlatResolver.ReleaseNoteAuthorResolver,
    },
  },
  // Users
  {
    path: 'users',
    component: UsersComponent,
    canActivate: [
      FlatGuards.UserAuthorGuard,
      FlatGuards.NavigationControlGuard
    ],
    data: {
      title: 'global.users',
    },
    resolve: {
      users: FlatResolver.UsersAuthorResolver,
    },
  },
  {
    path: 'user',
    component: UserDetailComponent,
    canActivate: [
      FlatGuards.UserAuthorGuard,
      FlatGuards.NavigationControlGuard
    ],
    data: {
      title: 'global.user',
    },
  },
  {
    path: 'user/:idSlug',
    component: UserDetailComponent,
    canActivate: [
      FlatGuards.UserAuthorGuard,
      FlatGuards.NavigationControlGuard
    ],
    data: {
      pathModelType: FlatModels.NavigationControlModels.PathModelType.enum.USER_ID,
      title: 'global.user',
    },
    resolve: {
      user: FlatResolver.UsersAuthorResolver,
    },
  },
  // Media
  {
    path: 'media',
    component: MediaLibraryComponent,
    canActivate: [
      FlatGuards.UserAuthorGuard,
      FlatGuards.NavigationControlGuard,
    ],
    data: {
      title: 'global.media_library',
    },
    resolve: {
      users: FlatResolver.UsersAuthorResolver,
      nodes: FlatResolver.ElearningSharedResolver
    },
  },
  // Matomo Stats
  {
    path: 'matomo-stats',
    component: MatomoStatsComponent,
    canActivate: [
      FlatGuards.UserAuthorGuard,
      FlatGuards.NavigationControlGuard,
    ],
    data: {
      title: 'global.matomo_stats',
    }
  },
  // Detached Pages
  {
    path: 'detached',
    component: DetachedPagesComponent,
    canActivate: [
      FlatGuards.UserAuthorGuard,
      FlatGuards.NavigationControlGuard
    ],
    data: {
      title: 'global.detached_slides',
    },
    resolve: {
      elearnings: FlatResolver.ElearningSharedResolver,
    }
  },
  // Import Stats
  {
    path: 'import-stats',
    component: ImportStatsComponent,
    canActivate: [
      FlatGuards.UserAuthorGuard,
      FlatGuards.NavigationControlGuard
    ],
    data: {
      title: 'global.import_deepzoom',
    },
    resolve: {
      elearnings: FlatResolver.ElearningSharedResolver,
    }
  },
  // Metadata
  {
    path: 'metadata',
    component: MetadataComponent,
    canActivate: [
      FlatGuards.UserAuthorGuard,
      FlatGuards.NavigationControlGuard
    ],
    data: {
      title: 'global.metadata',
    },
    resolve: {
      metadata: FlatResolver.MetadataSharedResolver,
    }
  },
  // Copyright
  {
    path: 'copyrights',
    component: CopyrightsComponent,
    canActivate: [
      FlatGuards.UserAuthorGuard,
      FlatGuards.NavigationControlGuard
    ],
    data: {
      title: 'global.copyrights',
    },
    resolve: {
      copyrights: FlatResolver.CopyrightsAuthorResolver,
    },
  },
  {
    path: 'copyright',
    component: CopyrightDetailComponent,
    canActivate: [
      FlatGuards.UserAuthorGuard,
      FlatGuards.NavigationControlGuard
    ],
    data: {
      title: 'global.copyright',
    },
  },
  {
    path: 'copyright/:idSlug',
    component: CopyrightDetailComponent,
    canActivate: [
      FlatGuards.UserAuthorGuard,
      FlatGuards.NavigationControlGuard
    ],
    data: {
      pathModelType: FlatModels.NavigationControlModels.PathModelType.enum.COPYRIGHT_ID,
      title: 'global.copyright',
    },
    resolve: {
      copyrights: FlatResolver.CopyrightsAuthorResolver,
    },
  },
  // Modality
  {
    path: 'modalities',
    component: ModalitiesComponent,
    canActivate: [
      FlatGuards.UserAuthorGuard,
      FlatGuards.NavigationControlGuard
    ],
    data: {
      title: 'global.modalities',
    },
    resolve: {
      modalities: FlatResolver.ModalitiesAuthorResolver,
    },
  },
  {
    path: 'modality',
    component: ModalityDetailComponent,
    canActivate: [
      FlatGuards.UserAuthorGuard,
      FlatGuards.NavigationControlGuard
    ],
    data: {
      title: 'global.modality',
    },
  },
  {
    path: 'modality/:idSlug',
    component: ModalityDetailComponent,
    canActivate: [
      FlatGuards.UserAuthorGuard,
      FlatGuards.NavigationControlGuard
    ],
    data: {
      pathModelType: FlatModels.NavigationControlModels.PathModelType.enum.MODALITY_ID,
      title: 'global.modality',
    },
    resolve: {
      modalities: FlatResolver.ModalitiesAuthorResolver,
    },
  },
  // Stain
  {
    path: 'stains',
    component: StainsComponent,
    canActivate: [
      FlatGuards.UserAuthorGuard,
      FlatGuards.NavigationControlGuard
    ],
    data: {
      title: 'global.stains',
    },
    resolve: {
      stains: FlatResolver.StainsAuthorResolver,
    },
  },
  {
    path: 'stain',
    component: StainDetailComponent,
    canActivate: [
      FlatGuards.UserAuthorGuard,
      FlatGuards.NavigationControlGuard
    ],
    data: {
      title: 'global.stain',
    },
  },
  {
    path: 'stain/:idSlug',
    component: StainDetailComponent,
    canActivate: [
      FlatGuards.UserAuthorGuard,
      FlatGuards.NavigationControlGuard
    ],
    data: {
      pathModelType: FlatModels.NavigationControlModels.PathModelType.enum.STAIN_ID,
      title: 'global.stain',
    },
    resolve: {
      stains: FlatResolver.StainsAuthorResolver,
    },
  },
  // Search
  {
    path: 'search',
    component: SearchComponent,
    canActivate: [
      FlatGuards.UserAuthorGuard,
      FlatGuards.NavigationControlGuard
    ],
    data: {
      title: 'global.search',
    },
    resolve: {
      nodes: FlatResolver.ElearningSharedResolver,
    },
  },
  // Node
  {
    path: 'node/:idSlug',
    component: ErrorsComponent,
    canActivate: [
      FlatGuards.RedirectControlGuard,
    ],
    data: {
      pathModelType: FlatModels.NavigationControlModels.PathModelType.enum.NODE_ID,
      title: 'global.slide',
    },
  },
  // Slide
  {
    path: 'slide/:idSlug',
    component: SlideComponent,
    canActivate: [
      FlatGuards.UserAuthorGuard,
      FlatGuards.NavigationControlGuard
    ],
    data: {
      pathModelType: FlatModels.NavigationControlModels.PathModelType.enum.SLIDE_ID,
      title: 'global.slide',
    },
    resolve: {
      elearnings: FlatResolver.ElearningSharedResolver,
      slide: FlatResolver.SlideSharedResolver,
      users: FlatResolver.UsersAuthorResolver,
      modalities: FlatResolver.ModalitiesAuthorResolver,
    }
  },
  // Chapter Slide
  {
    matcher: navigationMatcher,
    component: ChapterSlideComponent,
    canActivate: [
      FlatGuards.UserAuthorGuard,
      FlatGuards.NavigationControlGuard
    ],
    data: {
      pathModelType: FlatModels.NavigationControlModels.PathModelType.enum.SLIDE,
      index: true,
      title: 'global.chapter',
    },
    resolve: {
      elearnings: FlatResolver.ElearningSharedResolver,
      nodes: FlatResolver.NodesByRouteSharedResolver,
      users: FlatResolver.UsersAuthorResolver,
      modalities: FlatResolver.ModalitiesAuthorResolver,
    },
  },
  // Default
  {
    path: '**',
    component: ErrorsComponent,
    canActivate: [
      FlatGuards.NavigationControlGuard
    ],
    data: {
      title: 'global.route_not_found',
    }
  },
];

@NgModule({
  imports: [
    // Angular
    RouterModule.forRoot(appRoutes, {onSameUrlNavigation: 'ignore'}),
  ],
  exports: [
    // Angular
    RouterModule,
  ]
})
export class AppRoutingModule {
}
